import { useMemo, useState } from 'react'
import { Edit, Eye, Trash2, Search } from 'lucide-react'
import clsx from 'clsx'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import FacturaPreviewPDF from '../../../../../table/typesExportPDF/FacturaPreviewPDF'
import { getDteAbbreviation } from '../../utils/dteLabels'
import { formatDate } from '../../../../../../../components/utils/formatter'
import LucaModal from '../../../../../ui/LucaModal'
import Button from '../../../../../ui/CustomButton'

const formatCLP = value =>
  Number(value || 0).toLocaleString('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })

const ActionButton = ({ label, onClick, tone = 'slate', children }) => {
  const toneClass =
    tone === 'red'
      ? 'text-red-600 hover:bg-red-50'
      : tone === 'blue'
        ? 'text-blue-600 hover:bg-blue-50'
        : 'text-slate-600 hover:bg-slate-100'

  return (
    <button
      type='button'
      onClick={onClick}
      className={`rounded-lg p-2 transition ${toneClass}`}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  )
}

const DTE_NAMES = {
  33: 'Factura Electrónica',
  34: 'Factura Exenta',
  61: 'Nota de Crédito',
  56: 'Nota de Débito'
}

export default function VistaPreviaFacturas({
  currentBusiness,
  documents,
  isLoading,
  calculateTotals,
  onEdit,
  onDelete
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [previewDoc, setPreviewDoc] = useState(null)
  const [sortOrder, setSortOrder] = useState('desc')

  const filteredDocs = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    let filtered = documents

    if (term) {
      filtered = documents.filter(doc => {
        const receiver = doc.document_receiver || {}
        return [
          receiver.rut,
          receiver.business_name,
          receiver.address,
          doc.date_issued,
          doc.dte_type?.code
        ]
          .filter(Boolean)
          .some(value => String(value).toLowerCase().includes(term))
      })
    }

    return [...filtered].sort((a, b) => {
      const da = new Date(a.date_issued || 0).getTime()
      const db = new Date(b.date_issued || 0).getTime()
      return sortOrder === 'desc' ? db - da : da - db
    })
  }, [documents, searchTerm, sortOrder])

  const toggleSortDate = () => setSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'))

  const styles = {
    table: 'w-full border-collapse text-[0.725rem] leading-tight font-sans',
    thead: 'sticky top-0 z-10 bg-slate-50/90 backdrop-blur-md',
    th: 'py-4 px-4 text-center border-b border-slate-200 uppercase text-[0.6rem] font-bold text-slate-500 tracking-wider',
    tr: 'border-b border-slate-50 hover:bg-slate-50/50 transition-colors',
    td: 'px-4 py-2.5 align-middle',
    boldText: 'font-bold text-[0.7rem] text-slate-900',
    text: 'text-[0.64rem] font-medium text-slate-500',
    secondaryText: 'text-[0.6rem] text-slate-400'
  }

  const reportContent = (
    <div className='flex flex-col h-full min-h-0 overflow-hidden p-1'>
      <div className='mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='min-w-0'>
          <h3 className='text-md font-bold text-slate-900 leading-tight whitespace-nowrap'>
            Documentos por validar
          </h3>
          <p className='mt-0.5 text-[0.7rem] text-slate-500 font-medium'>
            A continuación podrá revisar un resumen del total de documentos que se van a emitir. En esta instancia podrá modificarlos, eliminarlos y revisarlos.
          </p>
        </div>

        <div className='relative w-full sm:w-60'>
          <Search size={14} className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400' />
          <input
            type='text'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder='Buscar...'
            className='w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-9 pr-3 text-[0.72rem] text-slate-700 outline-none transition focus:border-primary/40 '
          />
        </div>
      </div>

      <div className='flex-1 min-h-0 border border-slate-200 rounded-xl shadow-sm overflow-hidden bg-white flex flex-col'>
        <div className='overflow-auto flex-1 custom-scrollbar'>
          <table className={styles.table}>
            <thead className={styles.thead}>
              <tr>
                <th className={clsx(styles.th, 'text-left pl-6 w-[28%]')}>Cliente / Receptor</th>
                <th className={clsx(styles.th, 'w-[18%]')}>Documento</th>
                <th className={clsx(styles.th, 'w-[12%] cursor-pointer select-none')} onClick={toggleSortDate}>
                  <div className='flex items-center justify-center gap-1.5'>
                    <span>Fecha</span>
                    <KeyboardArrowDownIcon fontSize='inherit' className={clsx('transition-transform', sortOrder === 'desc' && 'rotate-180')} />
                  </div>
                </th>
                <th className={clsx(styles.th, 'w-[14%] text-right')}>Monto</th>
                <th className={clsx(styles.th, 'w-[16%]')}>Acciones</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-50'>
              {isLoading ? (
                <tr><td colSpan={5} className='py-20 text-center text-slate-400 font-medium'>Cargando documentos...</td></tr>
              ) : filteredDocs.length === 0 ? (
                <tr>
                  <td colSpan={5} className='py-20 text-center text-slate-400 font-medium'>
                    No se encontraron resultados.
                  </td>
                </tr>
              ) : (
                filteredDocs.map((doc, idx) => {
                  const totals = calculateTotals(doc)
                  const code = doc?.dte_type?.code || doc?.tipo_dte || '33'

                  return (
                    <tr key={idx} className={styles.tr}>
                      <td className={clsx(styles.td, 'pl-6')}>
                        <div className='flex items-center justify-center flex-col gap-0.5'>
                          <span className={clsx(styles.boldText, 'truncate block max-w-[240px]')} title={doc.document_receiver?.business_name}>
                            {doc.document_receiver?.business_name || 'Sin nombre'}
                          </span>
                          <span className={styles.secondaryText}>{doc.document_receiver?.rut || '—'}</span>
                        </div>
                      </td>
                      <td className={styles.td}>
                        <div className='flex flex-col items-center gap-1.5'>
                          <span className={clsx(
                            'inline-flex px-2 py-0.5 rounded-md text-[9px] font-black border',
                            code === '34'
                              ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                              : 'bg-blue-50 text-blue-600 border-blue-100'
                          )}>
                            {DTE_NAMES[code] || getDteAbbreviation(code)}
                          </span>
                          <span className={styles.secondaryText}>Fila {idx + 1}</span>
                        </div>
                      </td>
                      <td className={styles.td}>
                        <span className={clsx(styles.text, 'text-center block text-[10px]')}>
                          {doc.date_issued ? formatDate(doc.date_issued) : '—'}
                        </span>
                      </td>
                      <td className={styles.td}>
                        <div className='flex flex-col items-end leading-none'>
                          <span className={clsx(styles.boldText, 'text-[0.8rem]')}>${formatCLP(totals.total)}</span>
                          <div className='flex flex-col text-[0.58rem] mt-1 items-end gap-0.5'>
                            <span className='text-rose-500 font-semibold'>IVA: ${formatCLP(totals.iva)}</span>
                            <span className='text-slate-400'>Neto: ${formatCLP(totals.subtotal)}</span>
                          </div>
                        </div>
                      </td>
                      <td className={styles.td}>
                        <div className='flex justify-center gap-1'>
                          <ActionButton label='Editar' onClick={() => onEdit(idx)} tone='blue'>
                            <Edit size={16} />
                          </ActionButton>
                          <ActionButton label='Ver' onClick={() => setPreviewDoc(doc)}>
                            <Eye size={16} />
                          </ActionButton>
                          <ActionButton label='Eliminar' onClick={() => onDelete(idx)} tone='red'>
                            <Trash2 size={16} />
                          </ActionButton>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-hidden'>
      {reportContent}
      <LucaModal
        open={previewDoc != null}
        onClose={() => setPreviewDoc(null)}
        maxWidth="max-w-6xl"
        footer={
          <Button
            onClick={() => setPreviewDoc(null)}
            title="Cerrar"
            className="bg-[#0a0a2e] text-white px-8 py-1.5 rounded-full font-bold text-sm"
          />
        }
      >
        <div className="flex-1 flex flex-col min-h-0">
          <div className='flex-1 overflow-y-auto bg-slate-100 p-6 custom-scrollbar'>
            {previewDoc && <FacturaPreviewPDF document={previewDoc} currentBusiness={currentBusiness} />}
          </div>
        </div>
      </LucaModal>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; border: 2px solid #f1f1f1; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>
    </div>
  )
}